import React, { useEffect, useState } from 'react';
import { GENERATION_FAILED_MESSAGE } from '../api/pathsAPI';
import DraftPlanCard from './DraftPlanCard';
import './GenerationPanel.css';

// The four phases path_generation_service writes on the attempt. The card
// in the thread always says "Drafting…"; the phase is only shown here.
export const DRAFT_STEPS = [
  { id: 'understanding', label: 'Understanding your goal' },
  { id: 'drafting', label: 'Drafting your plan' },
  { id: 'validating', label: 'Checking dates and apps' },
  { id: 'saving', label: 'Saving' },
];

export const UPDATE_STEPS = [
  { id: 'understanding', label: 'Reading your change' },
  { id: 'drafting', label: 'Updating the plan' },
  { id: 'validating', label: 'Checking dates and apps' },
  { id: 'saving', label: 'Saving' },
];

export const GENERATION_STEPS = DRAFT_STEPS;

export const UPDATE_FAILURE_MESSAGE = "Couldn't update the draft";
export const DRAFT_REASSURANCE = "You can leave this page. It'll keep going, and the draft will be here when you're back.";

export function formatElapsed(startedAt, now = Date.now()) {
  const start = Date.parse(startedAt || '');
  const seconds = Number.isFinite(start) ? Math.max(0, Math.floor((now - start) / 1000)) : 0;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

// serverNow and startedAt are both database clocks. The offset is taken
// once per serverNow, so a client clock that is ahead or behind does not
// freeze the timer at 0:00 or start it high. A later status replaces it.
export function useServerOffset(serverNow) {
  const seen = React.useRef(null);
  const offset = React.useRef(0);
  if (serverNow !== seen.current) {
    seen.current = serverNow;
    const parsed = Date.parse(serverNow || '');
    offset.current = Number.isFinite(parsed) ? parsed - Date.now() : 0;
  }
  return offset.current;
}

export function updatedLabel(generatedAt, serverNow, now = Date.now()) {
  const generated = Date.parse(generatedAt || '');
  const server = Date.parse(serverNow || '');
  const offset = Number.isFinite(server) ? server - now : 0;
  if (!Number.isFinite(generated)) return 'updated just now';
  const seconds = Math.max(0, Math.floor((now + offset - generated) / 1000));
  if (seconds < 60) return 'updated just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `updated ${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `updated ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `updated ${days} day${days === 1 ? '' : 's'} ago`;
}

export function draftHeaderMeta({
  versionNumber, generating, updating, awaitingUpdate, pendingCount,
  generatedAt, serverNow, nothingApplied, now,
}) {
  if (!versionNumber) return null;
  const base = `Draft v${versionNumber}`;
  if (generating && updating) return `${base} · updating`;
  if (!generating && awaitingUpdate && pendingCount > 0) {
    const word = pendingCount === 1 ? 'change' : 'changes';
    return `${base} · ${pendingCount} ${word} waiting`;
  }
  if (!generating && updating && !nothingApplied) {
    return `${base} · ${updatedLabel(generatedAt, serverNow, now)}`;
  }
  return base;
}

export function couldntApplyLine(entry) {
  const reason = String(entry?.reason || '').trim().replace(/\.+$/, '');
  return `Couldn't apply: ${entry?.change} — ${reason}.`;
}

export function unappliedCountLine(count) {
  if (count === 1) return "1 change couldn't be applied";
  return `${count} changes couldn't be applied`;
}

function countLineOf(counts) {
  if (!counts) return '';
  return ['added', 'changed', 'removed'].filter((key) => counts[key]).map((key) => `${counts[key]} ${key}`).join(' · ');
}

export function useElapsed(startedAt, running, serverNow) {
  const [now, setNow] = useState(() => Date.now());
  const offset = useServerOffset(serverNow);
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);
  return formatElapsed(startedAt, now + offset);
}

function stepState(stepId, phase, steps) {
  const current = steps.findIndex((step) => step.id === phase);
  const index = steps.findIndex((step) => step.id === stepId);
  const at = current === -1 ? 0 : current;
  if (index < at) return 'done';
  if (index === at) return 'current';
  return 'upcoming';
}

// Stands in for the draft in the thread. The draft itself is in the panel.
export function GenerationFailureBanner({ onRetry }) {
  return (
    <div className="paths-gen-banner" role="alert">
      <p>{GENERATION_FAILED_MESSAGE}</p>
      <button type="button" className="paths-draft-btn" onClick={onRetry}>Try again</button>
    </div>
  );
}

export function GenerationThreadCard({
  title,
  startedAt,
  serverNow,
  generating,
  failed,
  panelOpen,
  onOpen,
  onRetry,
  updating = false,
  changeCount = 0,
  counts = null,
  nothingApplied = false,
  unappliedCount = 0,
}) {
  const elapsed = useElapsed(startedAt, generating, serverNow);
  const countLine = nothingApplied ? '' : countLineOf(counts);
  const waitingLine = changeCount === 1 ? '1 change' : `${changeCount} changes`;
  let heading = 'Draft ready';
  if (nothingApplied && !generating && !failed) heading = 'No changes made';
  else if (updating && failed) heading = UPDATE_FAILURE_MESSAGE;
  else if (failed) heading = GENERATION_FAILED_MESSAGE;
  else if (generating && updating) heading = `Updating draft… ${elapsed}`;
  else if (generating) heading = `Drafting… ${elapsed}`;
  else if (updating) heading = 'Draft updated';
  // The open panel is the live region. The card takes that over once the
  // panel is closed, including on a narrow screen where it is the way back.
  const role = panelOpen ? undefined : (failed ? 'alert' : 'status');
  return (
    <div className={`paths-gen-card${failed ? ' paths-gen-card-failed' : ''}`} role={role}>
      {generating && <span className="paths-gen-spinner" aria-hidden="true" />}
      <div className="paths-gen-card-copy">
        <p className="paths-gen-card-title">{title}</p>
        <p className="paths-gen-card-status">{heading}</p>
        {updating && generating && changeCount > 0 && (
          <p className="paths-gen-card-status">{waitingLine}</p>
        )}
        {nothingApplied && !generating && !failed && (
          <p className="paths-gen-card-status">{unappliedCountLine(unappliedCount)}</p>
        )}
        {updating && !nothingApplied && !generating && !failed && countLine && (
          <p className="paths-gen-card-status">{countLine}</p>
        )}
        {updating && failed && <p className="paths-gen-card-status">Your previous draft is unchanged</p>}
      </div>
      {failed ? (
        <button type="button" className="paths-draft-btn" onClick={onRetry}>Try again</button>
      ) : (
        <button type="button" className="paths-panel-toggle" onClick={onOpen}>
          {generating ? 'Show progress' : 'Show draft'}
        </button>
      )}
    </div>
  );
}

export default function GenerationPanel({
  title,
  phase,
  startedAt,
  serverNow,
  generating,
  failed,
  proposal,
  draftPath,
  mode,
  accepting,
  onAccept,
  onRequestChange,
  onDiscard,
  onRetry,
  onClose,
  updating = false,
  pendingChanges = [],
  baseline = null,
  awaitingUpdate = false,
  nothingApplied = false,
}) {
  const elapsed = useElapsed(startedAt, generating, serverNow);
  const steps = updating ? UPDATE_STEPS : DRAFT_STEPS;
  const showDraft = Boolean(proposal?.version) && !generating && (!failed || updating);
  const showDimmed = updating && generating && Boolean(baseline?.version);
  const unapplied = proposal?.changes?.unapplied || [];
  const counts = nothingApplied ? null : proposal?.changes?.diff?.counts;
  const summary = nothingApplied ? '' : (proposal?.changes?.summary || '');
  const countLine = countLineOf(counts);
  const showBanner = showDraft && !nothingApplied && Boolean(countLine || summary || unapplied.length);
  const [showChanges, setShowChanges] = useState(true);
  const versionId = proposal?.version?.id;
  useEffect(() => { setShowChanges(true); }, [versionId]);
  const meta = draftHeaderMeta({
    versionNumber: proposal?.version?.version_number || baseline?.version?.version_number,
    generating,
    updating,
    awaitingUpdate,
    pendingCount: pendingChanges.length,
    generatedAt: proposal?.version?.generated_at,
    serverNow,
    nothingApplied,
  });

  return (
    <div className="paths-gen-panel">
      <header className="paths-gen-header">
        <div className="paths-gen-heading">
          {meta && <p className="paths-gen-meta">{meta}</p>}
          {showDraft
            ? <p className="paths-gen-title">{title}</p>
            : <h2 className="paths-gen-title">{title}</h2>}
          {generating && <p className="paths-gen-elapsed">{elapsed}</p>}
        </div>
        {onClose && (
          <button type="button" className="path-view-btn path-view-icon-btn" onClick={onClose} aria-label="Close plan">
            Close
          </button>
        )}
      </header>

      {showDimmed && (
        <div className="paths-update-bar" role="status">
          <p>Updating draft… {elapsed}</p>
          {pendingChanges.length > 0 && (
            <ul>
              {pendingChanges.map((change) => <li key={change}>{change}</li>)}
            </ul>
          )}
        </div>
      )}

      {generating && (
        <div role="status">
          <ol className="paths-gen-steps">
            {steps.map((step) => {
              const state = stepState(step.id, phase, steps);
              return (
                <li key={step.id} className={`paths-gen-step paths-gen-step-${state}`} aria-current={state === 'current' ? 'step' : undefined}>
                  <span className={state === 'current' ? 'paths-gen-spinner' : 'paths-gen-mark'} aria-hidden="true">
                    {state === 'done' ? '✓' : ''}
                  </span>
                  {step.label}
                </li>
              );
            })}
          </ol>
          {!showDimmed && (
            <div className="paths-gen-placeholders" aria-hidden="true">
              <span className="paths-gen-placeholder" />
              <span className="paths-gen-placeholder" />
              <span className="paths-gen-placeholder paths-gen-placeholder-short" />
              <span className="paths-gen-placeholder" />
            </div>
          )}
          <p className="paths-gen-footer">
            {updating ? 'Available when the update finishes' : DRAFT_REASSURANCE}
          </p>
        </div>
      )}

      {failed && !generating && (
        <div className="paths-gen-failure" role="alert">
          <p>{updating ? "Couldn't update the draft. Your previous draft is unchanged." : GENERATION_FAILED_MESSAGE}</p>
          <button type="button" className="paths-draft-btn" onClick={onRetry}>Try again</button>
        </div>
      )}

      {showDimmed && (
        <div className="paths-gen-draft paths-gen-draft-dimmed">
          <DraftPlanCard
            path={draftPath || { title }}
            version={baseline.version}
            items={baseline.items}
            accepting
            mode={mode}
            readOnly
          />
        </div>
      )}

      {showBanner && (
        <div className="paths-what-changed" role="status">
          <div className="paths-what-changed-row">
            <b>What changed</b>
            {countLine && <span className="paths-what-changed-counts">{countLine}</span>}
            <button type="button" className="paths-panel-toggle" onClick={() => setShowChanges((value) => !value)}>
              {showChanges ? 'Hide changes' : 'Show changes'}
            </button>
          </div>
          {summary && <p>{summary}</p>}
          {unapplied.map((entry) => <p key={entry.change}>{couldntApplyLine(entry)}</p>)}
        </div>
      )}

      {showDraft && nothingApplied && unapplied.length > 0 && (
        <div className="paths-gen-unapplied" role="status">
          {unapplied.map((entry) => <p key={entry.change}>{couldntApplyLine(entry)}</p>)}
        </div>
      )}

      {showDraft && (
        <div className="paths-gen-draft">
          <DraftPlanCard
            path={draftPath || { title }}
            version={proposal.version}
            items={proposal.items}
            changes={nothingApplied ? { ...proposal.changes, diff: null } : proposal.changes}
            showChanges={nothingApplied ? false : showChanges}
            onAccept={onAccept}
            onRequestChange={onRequestChange}
            onDiscard={onDiscard}
            accepting={accepting}
            mode={mode}
          />
        </div>
      )}
    </div>
  );
}
